import React, { useRef, useEffect, forwardRef, useImperativeHandle, ForwardedRef, MutableRefObject, useState, SyntheticEvent, useMemo } from 'react';
import { useMethods } from '@lhb/hook';
import { isNotEmpty } from '@lhb/func';
import styles from './index.module.less';
import cs from 'classnames';
import videojs from 'video.js';
import 'video.js/dist/video-js.min.css';
import ResizeObserver from 'resize-observer-polyfill';
import { isMicro, getCss } from '../../config-v2';

// 模块级注册，防止热更新重复注册
if (!videojs.getComponent('RotateButton')) {
  const Button = videojs.getComponent('Button') as any;
  class RotateButton extends Button {
    buildCSSClass() { return `vjs-rotate-button ${super.buildCSSClass()}`; }
    handleClick() { this.options_.onRotate?.(); }
  }
  videojs.registerComponent('RotateButton', RotateButton as any);
}

export interface V2VideoHandles {
  /**
   * @description video实例，请查看文档 https://docs.videojs.com/audiotrack#event
   */
  video: MutableRefObject<any>;
  /**
   * @description 更新src地址
   */
  replaceSrc: (src: string) => void;
}
export interface V2VideoPlayerProps {
  /**
   * @description 视频地址
   */
  src: string;
  /**
   * @description 视频模块宽度
   * @default 300px
   */
  width?: string | number;
  /**
   * @description 视频模块高度
   * @default 300px
   */
  height?: string | number;
  /**
   * @description videojs配置项，详情请查看 https://docs.videojs.com/#options
   */
  options?: any;
  /**
   * @description 样式模式, 默认 标准模式
   * @type ['base' | 'young']
   * @default base
   */
  styleType?: string;
  /**
   * @description 是否支持画中画播放
   */
  pictureInPictureToggle?: boolean;
  /**
   * @description 是否在控制栏显示旋转按钮
   * @default true
   */
  rotatable?: boolean;
  /**
   *@description ref实例
   */
  ref?: any;
  /**
   * @description 是否在视频右下角展示「视频时长」角标，控制条可见时角标会自动上抬避让进度条
   */
  showDuration?: boolean;
  /**
   * @description 播放事件
   */
  onPlay?: () => void;
  /**
   * @description 暂停事件
   */
  onPause?: () => void;
  /**
   * @description 结束事件
   */
  onEnded?: () => void;
  /**
   * @description 用户活动事件
   */
  onUserActive?: () => void;
  /**
   * @description 用户不活动事件
   */
  onUserInactive?: () => void;
}
/**
* @description 便捷文档地址
* @see https://reactpc.lanhanba.net/components/Data/v2video-player
*/
const V2VideoPlayer: React.FC<V2VideoPlayerProps> = forwardRef(({
  src,
  width = '300px',
  height = '300px',
  options = {},
  styleType,
  pictureInPictureToggle = true,
  rotatable = true,
  showDuration = false,
  onPlay,
  onPause,
  onEnded,
  onUserActive,
  onUserInactive,
}, ref: ForwardedRef<V2VideoHandles>) => {

  useImperativeHandle(ref, () => ({
    video: videoRef, // 不能videoRef.current
    replaceSrc: (src) => methods.replaceSrc(src)
  }));

  const videoRef = useRef<any>(null);
  const playerRef = useRef<any>(null);
  const rotationRef = useRef<0 | 90 | 180 | 270>(0);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const applyRotation = (deg: 0 | 90 | 180 | 270) => {
    const tech = playerRef.current?.tech_?.el_ as HTMLVideoElement | null;
    if (!tech) return;
    const container = tech.closest('.video-js') as HTMLElement;
    const W = container.offsetWidth;
    const H = container.offsetHeight;
    if (deg === 90 || deg === 270) {
      tech.style.width = `${H}px`;
      tech.style.height = `${W}px`;
      tech.style.top = `${(H - W) / 2}px`;
      tech.style.left = `${(W - H) / 2}px`;
    } else {
      tech.style.width = '100%';
      tech.style.height = '100%';
      tech.style.top = '0';
      tech.style.left = '0';
    }
    tech.style.transform = deg === 0 ? '' : `rotate(${deg}deg)`;
  };

  /** 视频时长（秒），由隐藏 video 的 loadedmetadata 解析得到 */
  const [duration, setDuration] = useState<number>(0);
  /** 暂停态：null = 未开始 / false = 播放中 / true = 暂停或结束 —— videojs 暂停时控制条始终可见 */
  const [paused, setPaused] = useState<boolean | null>(null);
  /** 用户活动态：videojs useractive / userinactive 推导，活动中控制条会显现 */
  const [userActive, setUserActive] = useState<boolean>(false);

  /**
   * 控制条是否可见
   * 规则：未开始时仅看 userActive；开始后 = 暂停 OR 用户活动中
   * @returns {boolean} true 表示底部控制条可见，对外用于决定角标位移
   */
  const barVisible = useMemo(() => {
    if (!isNotEmpty(paused)) return userActive;
    return paused || userActive;
  }, [paused, userActive]);

  useEffect(() => {
    if (isMicro) {
      if ((window as any).__POWERED_BY_QIANKUN__) {
        setTimeout(() => {
          getCss('videoJS', 'html');
        });
      }
    }
    methods.init();
    return () => {
      resizeObserverRef.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const methods = useMethods({

    init: () => {
      const _children: any[] = [
        { name: 'playToggle' }, // 播放按钮
        {
          name: 'volumePanel', // 音量控制
          inline: false, // 不使用水平方式
        },
        { name: 'currentTimeDisplay' }, // 当前已播放时间
        // { name: 'durationDisplay' }, // 总时间
        { name: 'progressControl' }, // 播放进度条
      ];
      // young 模式下画中画也加入控制栏，但非全屏时通过 DOM 隐藏
      if (pictureInPictureToggle) {
        _children.push({
          name: 'pictureInPictureToggle'
        });
      }
      if (rotatable) {
        _children.push({
          name: 'RotateButton',
          onRotate: () => {
            const next = ((rotationRef.current + 90) % 360) as 0 | 90 | 180 | 270;
            rotationRef.current = next;
            applyRotation(next);
          },
        });
      }
      _children.push({
        name: 'FullscreenToggle'// 支持全屏，
      });
      const _option = {
        controls: true,
        // notSupportedMessage: '此视频暂无法播放，当前仅支持mp4，ogg，webm，mpeg4 格式',
        controlBar: {
          // timeDivider: true,//是否显示时间控制条，默认为true
          // remainingTimeDisplay: false,//是否显示剩余时间，默认为true
          fullscreenToggle: false, // 全屏按钮
          children: _children
        }
      };
      if (!playerRef.current) {
        const videoElement = videoRef.current;
        if (!videoElement) return;
        const player = playerRef.current = videojs(videoElement, Object.assign(_option, options), () => {
        });
        methods.onReadyPlay(player);

        // 统一对外抛出播放状态事件，供父组件控制如水印/角标位移等交互；同时维护内部 paused/userActive，驱动时长角标位移
        player.on('play', () => {
          setPaused(false);
          setUserActive(true);
          onPlay?.();
        });
        player.on('pause', () => {
          setPaused(true);
          onPause?.();
        });
        player.on('ended', () => {
          setPaused(true);
          onEnded?.();
        });
        // 用户活动 / 不活动事件：父组件可据此推导控制条是否可见
        player.on('useractive', () => {
          setUserActive(true);
          onUserActive?.();
        });
        player.on('userinactive', () => {
          setUserActive(false);
          onUserInactive?.();
        });
        // 用 ResizeObserver 监听容器尺寸变化（全屏进出），尺寸稳定后重算旋转
        if (rotatable) {
          const containerEl = player.el() as HTMLElement;
          resizeObserverRef.current = new ResizeObserver(() => {
            if (rotationRef.current !== 0) {
              applyRotation(rotationRef.current);
            }
          });
          resizeObserverRef.current.observe(containerEl);
        }

        // young 模式下：旋转和画中画按钮仅全屏时显示
        if (styleType === 'young') {
          const syncYoungBtns = () => {
            const isFs = player.isFullscreen();
            const rotateBtn = player.el()?.querySelector('.vjs-rotate-button') as HTMLElement | null;
            const pipBtn = player.el()?.querySelector('.vjs-picture-in-picture-control') as HTMLElement | null;
            if (rotateBtn) rotateBtn.style.display = isFs ? '' : 'none';
            if (pipBtn) pipBtn.style.display = isFs ? '' : 'none';
          };
          player.ready(() => syncYoungBtns());
          player.on('fullscreenchange', syncYoungBtns);
        }
      }
    },

    onReadyPlay: (palyer) => {
      videoRef.current = palyer;
      // palyer.play();
    },

    replaceSrc: (newSrc: string) => {
      videoRef.current?.src?.([
        { src: newSrc, type: 'video/mp4' },
        { src: newSrc, type: 'video/ogg' },
        { src: newSrc, type: 'video/webm' },
        { src: newSrc, type: 'video/mpeg4' },
      ]);
    },

    /**
     * 隐藏 video 元素元数据加载完成回调，记录视频时长
     * @param {SyntheticEvent<HTMLVideoElement>} e React 合成事件
     */
    handleLoadedMetadata(e: SyntheticEvent<HTMLVideoElement>) {
      const sec = e.currentTarget.duration;
      // 部分流媒体格式拿到的 duration 是 Infinity / NaN，需做有效性判断
      if (Number.isFinite(sec)) {
        setDuration(sec);
      }
    },
    /**
     * 将秒数格式化为 mm:ss 字符串
     * @param {number} sec 秒数
     * @returns {string} 形如 "00:28"
     */
    formatDuration(sec: number): string {
      const total = Math.floor(sec);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },

  });

  return (<div
    className={cs([
      styles.V2VideoPlayer,
      styleType === 'young' && styles.V2VideoPlayerYoung,
      rotatable && styles.V2VideoPlayerRotatable,
    ])}
    style={{
      width,
      height,
    }}
  >
    <video
      style={{ height: '100%', width: '100%' }}
      ref={videoRef}
      className='video-js vjs-big-play-centered'
    >
      <source src={src} type='video/mp4' />
      <source src={src} type='video/ogg'/>
      <source src={src} type='video/webm'/>
      <source src={src} type='video/mpeg4'/>
    </video>
    {/* 隐藏 video 元素：仅用于读取时长元数据（部分流媒体下 videojs 自身的 duration 不够稳定，独立解析更可靠） */}
    {!!showDuration && !!src && <video
      src={src}
      preload='metadata'
      style={{ display: 'none' }}
      onLoadedMetadata={methods.handleLoadedMetadata}
    />}
    {!!showDuration && !!src && !!duration && (
      <div className={cs(styles.videoDurationLabel, barVisible && styles.durationLabelShifted)}>{methods.formatDuration(duration)}</div>
    )}
  </div>);
});

export default V2VideoPlayer;
